import React, { useState, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Upload, FileText, CheckCircle, AlertCircle, Users, Download, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

export default function ImportData() {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [step, setStep] = useState(1); // 1: Upload, 2: Preview, 3: Result
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      parseCSVPreview(selectedFile);
    }
  };

  const parseCSVPreview = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n');
      
      // Find the header row (contains "Employee No.")
      let headerIndex = -1;
      for (let i = 0; i < lines.length; i++) {
        if (lines[i].includes('Employee No.')) {
          headerIndex = i;
          break;
        }
      }
      
      if (headerIndex === -1) {
        toast.error('Invalid CSV format. Could not find the Employee No. / Name column. Please download the MyHR Orbit template above and use it as your starting point.');
        return;
      }
      
      // Parse headers
      const headers = parseCSVLine(lines[headerIndex]);
      
      // Parse data rows
      const employees = [];
      for (let i = headerIndex + 1; i < lines.length; i++) {
        if (lines[i].trim()) {
          const values = parseCSVLine(lines[i]);
          if (values[0] && values[1]) { // Has employee no and name
            const employee = {};
            headers.forEach((header, index) => {
              employee[header] = values[index] || '';
            });
            employees.push(employee);
          }
        }
      }
      
      setPreview({
        totalEmployees: employees.length,
        employees: employees.slice(0, 5), // Show first 5 for preview
        allEmployees: employees,
        headers: headers
      });
      setStep(2);
    };
    reader.readAsText(file);
  };

  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const handleImport = async () => {
    if (!preview || !preview.allEmployees) return;
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const response = await axios.post(`${API}/import/kakitangan`, {
        employees: preview.allEmployees
      });
      
      setImportResult(response.data);
      setStep(3);
      toast.success(`Successfully imported ${response.data.imported} employees!`);
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import data');
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setFile(null);
    setPreview(null);
    setImportResult(null);
    setStep(1);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    // Iter88 (Ankur 09-Jul-2026) — `Joined Date` is a REQUIRED column
    // (the import now rejects rows without a valid parseable join date).
    // Accepts DD/MM/YYYY (canonical) or YYYY-MM-DD. Column header MUST
    // remain exactly `Joined Date` so the backend key lookup matches.
    const template = `Employee No.,Name,Employment Type,Position,Position Level,Email,Department,Subsidary,Joined Date,Termination Date,NRIC,Bank Account,EPF Number,Income Tax Number,SOCSO Number,Basic Salary
EMP001,John Doe,Full Time,Software Engineer,,john@example.com,IT,,01/01/2024,,901234567890,1234567890,12345678,SG12345678,,5000.00`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'employee_import_template.csv';
    a.click();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Import Data</h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            {/* Iter89 (Ankur 09-Jul-2026) — Removed all Kakitangan.com
                references. MyHR Orbit is the product; the import tool is
                a generic CSV/Excel employee onboarding wizard. */}
            Bulk-import employees into MyHR Orbit from a CSV or Excel export
          </p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center space-x-4 mb-8">
        {[
          { num: 1, label: 'Upload File' },
          { num: 2, label: 'Preview Data' },
          { num: 3, label: 'Import Complete' }
        ].map((s, i) => (
          <React.Fragment key={s.num}>
            <div className={`flex items-center space-x-2 ${step >= s.num ? 'text-emerald-600' : 'text-slate-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                step >= s.num ? 'bg-emerald-600 text-white' : 'bg-slate-200 dark:bg-slate-700'
              }`}>
                {step > s.num ? <CheckCircle className="h-5 w-5" /> : s.num}
              </div>
              <span className="hidden sm:inline font-medium">{s.label}</span>
            </div>
            {i < 2 && <ArrowRight className="h-5 w-5 text-slate-300" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Upload */}
      {step === 1 && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-8">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mb-4">
              <Upload className="h-8 w-8 text-emerald-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
              Upload Employee CSV
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              Upload a CSV of your employees to bulk-onboard them into MyHR Orbit.
            </p>

            <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl p-8 hover:border-emerald-500 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
                id="csv-upload"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <FileText className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600 dark:text-slate-400 mb-2">
                  <span className="text-emerald-600 font-semibold">Click to upload</span> or drag and drop
                </p>
                <p className="text-sm text-slate-500">CSV file with employee records</p>
              </label>
            </div>

            <div className="mt-6 flex items-center justify-center space-x-4">
              <Button variant="outline" onClick={downloadTemplate}>
                <Download className="h-4 w-4 mr-2" />
                Download Template
              </Button>
            </div>

            {/* Iter88 — Required-column hint. Prevents the tester's
                "Invalid Date" bug where an import with blank Joined Date
                silently defaulted the join date and broke Employee
                Details rendering. */}
            <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
              <span className="font-semibold text-slate-700 dark:text-slate-300">Required columns:</span>{' '}
              <span className="text-red-600 dark:text-red-400">Name</span>,{' '}
              <span className="text-red-600 dark:text-red-400">Joined Date</span>{' '}
              (DD/MM/YYYY or YYYY-MM-DD). Rows missing either will be skipped with an error.
            </p>

            {/* Iter89 — Replaced the "How to export from Kakitangan"
                steps with a MyHR Orbit-branded, generic CSV workflow. */}
            <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-left">
              <h4 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">How to prepare your CSV</h4>
              <ol className="text-sm text-blue-800 dark:text-blue-400 space-y-1 list-decimal list-inside">
                <li>Click <strong>Download Template</strong> above to get the MyHR Orbit CSV template</li>
                <li>Open the template in Excel, Numbers or Google Sheets</li>
                <li>Fill in one row per employee — <strong>Name</strong> and <strong>Joined Date</strong> are required</li>
                <li>Save the file as <strong>CSV (Comma delimited)</strong></li>
                <li>Upload the CSV here — you&apos;ll be able to preview the rows before importing</li>
              </ol>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Preview */}
      {step === 2 && preview && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                Preview Import Data
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                Found <span className="font-semibold text-emerald-600">{preview.totalEmployees}</span> employees to import
              </p>
            </div>
            <Button variant="outline" onClick={resetImport}>
              Upload Different File
            </Button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4">
              <Users className="h-6 w-6 text-emerald-600 mb-2" />
              <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">{preview.totalEmployees}</p>
              <p className="text-sm text-emerald-600 dark:text-emerald-500">Total Employees</p>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <FileText className="h-6 w-6 text-blue-600 mb-2" />
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">{preview.headers?.length || 0}</p>
              <p className="text-sm text-blue-600 dark:text-blue-500">Data Fields</p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
              <CheckCircle className="h-6 w-6 text-purple-600 mb-2" />
              <p className="text-2xl font-bold text-purple-700 dark:text-purple-400">Ready</p>
              <p className="text-sm text-purple-600 dark:text-purple-500">To Import</p>
            </div>
          </div>

          {/* Preview Table */}
          <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-900">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-slate-900 dark:text-slate-100">Employee No.</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-900 dark:text-slate-100">Name</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-900 dark:text-slate-100">Position</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-900 dark:text-slate-100">Department</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-900 dark:text-slate-100">Email</th>
                    <th className="px-4 py-3 text-right font-semibold text-slate-900 dark:text-slate-100">Basic Salary</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {preview.employees.map((emp, index) => (
                    <tr key={index} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                      <td className="px-4 py-3 text-slate-900 dark:text-slate-100">{emp['Employee No.']}</td>
                      <td className="px-4 py-3 text-slate-900 dark:text-slate-100 font-medium">{emp['Name']}</td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{emp['Position']}</td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{emp['Department']}</td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{emp['Email']}</td>
                      <td className="px-4 py-3 text-right text-slate-900 dark:text-slate-100">
                        RM {parseFloat(emp['Basic Salary'] || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {preview.totalEmployees > 5 && (
              <div className="px-4 py-3 bg-slate-50 dark:bg-slate-900 text-center text-sm text-slate-500">
                ... and {preview.totalEmployees - 5} more employees
              </div>
            )}
          </div>

          {/* Field Mapping Info */}
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-amber-900 dark:text-amber-300 mb-2">📋 Data Mapping</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-amber-800 dark:text-amber-400">
              <div>Employee No. → Employee ID</div>
              <div>Name → Full Name</div>
              <div>NRIC → IC Number</div>
              <div>Email → Email</div>
              <div>Position → Position</div>
              <div>Department → Department</div>
              <div>Joined Date → Hire Date</div>
              <div>Basic Salary → Basic Salary</div>
              <div>EPF Number → EPF Number</div>
              <div>SOCSO Number → SOCSO Number</div>
              <div>Income Tax Number → Tax Number</div>
              <div>Bank Account → Bank Account</div>
            </div>
          </div>

          {/* Import Button */}
          <div className="flex items-center justify-end space-x-4">
            <Button variant="outline" onClick={resetImport}>
              Cancel
            </Button>
            <Button 
              onClick={handleImport} 
              disabled={importing}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {importing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Import {preview.totalEmployees} Employees
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Result */}
      {step === 3 && importResult && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-8">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-emerald-600" />
            </div>
            <h3 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
              Import Complete!
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              Your employee data has been successfully imported
            </p>

            {/* Result Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4">
                <p className="text-3xl font-bold text-emerald-700 dark:text-emerald-400">{importResult.imported}</p>
                <p className="text-sm text-emerald-600">Employees Imported</p>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                <p className="text-3xl font-bold text-blue-700 dark:text-blue-400">{importResult.updated || 0}</p>
                <p className="text-sm text-blue-600">Records Updated</p>
              </div>
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4">
                <p className="text-3xl font-bold text-amber-700 dark:text-amber-400">{importResult.skipped || 0}</p>
                <p className="text-sm text-amber-600">Skipped (Duplicates)</p>
              </div>
            </div>

            {/* Errors if any */}
            {importResult.errors && importResult.errors.length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 mb-6 text-left">
                <h4 className="font-semibold text-red-900 dark:text-red-300 mb-2 flex items-center">
                  <AlertCircle className="h-5 w-5 mr-2" />
                  Import Errors
                </h4>
                <ul className="text-sm text-red-800 dark:text-red-400 space-y-1 list-disc list-inside">
                  {importResult.errors.slice(0, 5).map((error, i) => (
                    <li key={i}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-center justify-center space-x-4">
              <Button variant="outline" onClick={resetImport}>
                Import More Data
              </Button>
              <Button 
                onClick={() => window.location.href = '/dashboard/employees'}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Users className="h-4 w-4 mr-2" />
                View Employees
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
